import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';

import { ApiError, api, employmentTypeLabels, formValues, type Employee, type EmployeeInput, type EmploymentType } from '../api.js';
import { navigate } from '../router.js';
import { pageStyles } from '../shared-styles.js';
import '../components/audit-dialog.js';
import type { AuditDialog } from '../components/audit-dialog.js';
import '../components/vacation-dialog.js';
import type { VacationDialog } from '../components/vacation-dialog.js';

const employmentTypes = Object.entries(employmentTypeLabels) as [EmploymentType, string][];

@customElement('employees-page')
export class EmployeesPage extends LitElement {
  static styles = pageStyles;

  @state() private items: Employee[] = [];
  @state() private docCounts: Record<string, number> = {};
  @state() private loading = true;
  @state() private error = '';

  @state() private dialogOpen = false;
  @state() private editing: Employee | null = null;
  @state() private saving = false;
  @state() private formError = '';
  @state() private fieldErrors: Record<string, string> = {};

  connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.error = '';
    try {
      [this.items, this.docCounts] = await Promise.all([api.get<Employee[]>('/api/employees'), api.documentCounts('employee')]);
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  private openNew() {
    this.editing = null;
    this.resetFormState();
    this.dialogOpen = true;
  }

  private openEdit(item: Employee) {
    this.editing = item;
    this.resetFormState();
    this.dialogOpen = true;
  }

  private history(id: number, label: string) {
    void (this.renderRoot.querySelector('audit-dialog') as AuditDialog).show('employee', id, label);
  }

  private vacation(item: Employee) {
    void (this.renderRoot.querySelector('vacation-dialog') as VacationDialog).show(item.id, item.name);
  }

  private resetFormState() {
    this.formError = '';
    this.fieldErrors = {};
    this.saving = false;
  }

  private async save(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const v = formValues(form);
    const input: EmployeeInput = {
      name: v.name ?? '',
      email: v.email ?? '',
      position: v.position,
      address: v.address,
      phone: v.phone,
      embg: v.embg,
      bank_account: v.bank_account,
      start_date: v.start_date ?? '',
      end_date: v.end_date,
      employment_type: (v.employment_type ?? 'full_time') as EmploymentType,
      hours_per_day: v.hours_per_day,
    };
    this.saving = true;
    this.formError = '';
    this.fieldErrors = {};
    try {
      if (this.editing) {
        await api.put(`/api/employees/${this.editing.id}`, input);
      } else {
        await api.post('/api/employees', input);
      }
      this.dialogOpen = false;
      await this.load();
    } catch (err) {
      if (err instanceof ApiError) {
        this.formError = err.message;
        this.fieldErrors = err.fields;
      } else {
        this.formError = (err as Error).message;
      }
    } finally {
      this.saving = false;
    }
  }

  private async deleteItem(item: Employee) {
    if (!confirm(`Delete employee "${item.name}"?`)) return;
    try {
      await api.delete(`/api/employees/${item.id}`);
      await this.load();
    } catch (err) {
      this.error = (err as Error).message;
    }
  }

  render() {
    return html`
      <div class="page-header">
        <h2>Employees</h2>
        <span class="spacer"></span>
        <wa-button variant="brand" @click=${this.openNew}>New employee</wa-button>
      </div>
      ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
      ${this.renderTable()} ${this.renderDialog()}
      <audit-dialog></audit-dialog>
      <vacation-dialog></vacation-dialog>
    `;
  }

  private renderTable() {
    if (this.loading) return html`<wa-spinner></wa-spinner>`;
    if (this.items.length === 0) return html`<div class="empty">No employees yet.</div>`;
    return html`
      <div class="table-wrap"><table class="cards">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Position</th>
            <th>Type</th>
            <th>Bank account</th>
            <th>Start</th>
            <th>End</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${this.items.map(
            (e) => html`
              <tr>
                <td data-label="Name">${e.name}</td>
                <td class="wrap" data-label="Email">${e.email}</td>
                <td class="m-hide" data-label="Role">${e.position ?? html`<span class="muted">—</span>`}</td>
                <td data-label="Type">${employmentTypeLabels[e.employment_type]}</td>
                <td class=${e.bank_account ? 'mono' : 'm-hide'} data-label="Account">${e.bank_account ?? html`<span class="muted">—</span>`}</td>
                <td class="date" data-label="Start">${e.start_date}</td>
                <td class=${e.end_date ? 'date' : 'date m-hide'} data-label="End">${e.end_date ?? html`<span class="muted">—</span>`}</td>
                <td class="actions">
                  <wa-button size="small" appearance="plain" @click=${() => navigate(`/documents?employee_id=${e.id}`)}>Documents (${this.docCounts[e.id] ?? 0})</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.vacation(e)}>Vacation</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.history(e.id, e.name)}>History</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.openEdit(e)}>Edit</wa-button>
                  <wa-button size="small" appearance="plain" variant="danger" @click=${() => this.deleteItem(e)}>Delete</wa-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table></div>
    `;
  }

  // wa-select and other children emit the same bubbling wa-after-hide when
  // their dropdown closes; only the dialog's own event should close it.
  private onDialogHidden = (e: Event) => {
    if (e.target === e.currentTarget) this.dialogOpen = false;
  };

  private renderDialog() {
    const e = this.editing;
    const err = (f: string) => this.fieldErrors[f] ?? '';
    return html`
      <wa-dialog
        label=${e ? 'Edit employee' : 'New employee'}
        ?open=${this.dialogOpen}
        @wa-after-hide=${this.onDialogHidden}
        style="--width: min(40rem, calc(100vw - 1.5rem));"
      >
        <form id="employee-form" @submit=${this.save}>
          <wa-input name="name" label="Name" required .value=${e?.name ?? ''} hint=${err('name')}></wa-input>
          <wa-input name="email" label="Workspace email" type="email" required .value=${e?.email ?? ''} hint=${err('email')}></wa-input>
          <wa-input name="position" label="Position" .value=${e?.position ?? ''}></wa-input>
          <wa-select name="employment_type" label="Employment type" .value=${e?.employment_type ?? 'full_time'} hint=${err('employment_type') || 'M2 for full-time, services agreement otherwise'}>
            ${employmentTypes.map(([value, label]) => html`<wa-option value=${value}>${label}</wa-option>`)}
          </wa-select>
          <wa-input name="hours_per_day" label="Hours per working day" inputmode="decimal" placeholder="7.5" .value=${e?.hours_per_day ?? '7.5'} hint=${err('hours_per_day') || 'Expected hours; 0 skips shortfall alerts'}></wa-input>
          <wa-input name="phone" label="Phone" .value=${e?.phone ?? ''}></wa-input>
          <wa-input class="full" name="address" label="Address" .value=${e?.address ?? ''}></wa-input>
          <wa-input name="start_date" label="Start date" type="date" required .value=${e?.start_date ?? ''} hint=${err('start_date')}></wa-input>
          <wa-input name="end_date" label="End date" type="date" .value=${e?.end_date ?? ''} hint=${err('end_date')}></wa-input>
          <wa-input name="embg" label="EMBG (personal ID)" inputmode="numeric" maxlength="13" .value=${e?.embg ?? ''} hint=${err('embg') || 'Stored encrypted'}></wa-input>
          <wa-input class="full" name="bank_account" label="Bank account" inputmode="numeric" placeholder="200003056063659 or MK07200003056063659" .value=${e?.bank_account ?? ''} hint=${err('bank_account') || 'Transfers to this account are backed by the employee on statement import'}></wa-input>
          ${this.formError ? html`<wa-callout class="form-error" variant="danger">${this.formError}</wa-callout>` : nothing}
        </form>
        <div slot="footer" class="footer">
          <wa-button appearance="outlined" @click=${() => (this.dialogOpen = false)}>Cancel</wa-button>
          <wa-button variant="brand" type="submit" form="employee-form" ?loading=${this.saving}>Save</wa-button>
        </div>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'employees-page': EmployeesPage;
  }
}
